'use client';
import { useMDXComponent } from '@content-collections/mdx/react';
import { BlurImageWithEffect, CodeBlock, Kbd } from '@mcdougald/ui/src';
import { type ComponentProps } from 'react';

import ImageZoom from '../../image-zoom';
import CustomLink from '../../link';

import {
  BlogList,
  BlogListItem,
  BlogListNumbered,
  type BlogListProps,
} from './blog-list';
import Heading from './heading';
import ItemGrid from './item-grid';
import LinkCard from './link-card';
import Logo from './logo';
import Table from './table';
import TreeView from './tree-view';
import Video from './video';

type MdxProps = {
  code: string;
};

const components = {
  h1: (props: ComponentProps<'h2'>) => <Heading as="h1" {...props} />,
  h2: (props: ComponentProps<'h2'>) => <Heading as="h2" {...props} />,
  h3: (props: ComponentProps<'h3'>) => <Heading as="h3" {...props} />,
  h4: (props: ComponentProps<'h4'>) => <Heading as="h4" {...props} />,
  h5: (props: ComponentProps<'h5'>) => <Heading as="h5" {...props} />,
  h6: (props: ComponentProps<'h6'>) => <Heading as="h6" {...props} />,
  ol: (props: BlogListProps) => BlogListNumbered(props),
  ul: (props: BlogListProps) => BlogList(props),
  li: (props: BlogListProps) => BlogListItem(props),
  a: (props: ComponentProps<'a'>) => {
    const { children, ...rest } = props;

    return (
      <CustomLink
        className="hover:text-foreground text-anchor no-underline transition-colors"
        {...rest}
      >
        {children}
      </CustomLink>
    );
  },
  Image: (props: ComponentProps<typeof BlurImageWithEffect>) => {
    const { alt, ...rest } = props;

    return (
      <>
        <ImageZoom>
          <BlurImageWithEffect
            className="rounded-lg border"
            alt={alt}
            {...rest}
          />
        </ImageZoom>
        <figcaption className="mt-4 text-center">{alt}</figcaption>
      </>
    );
  },
  pre: CodeBlock,
  // Custom components
  Table,
  ItemGrid,
  Video,
  LinkCard,
  Logo,
  TreeView,
  Kbd,
};

/**
 * Renders MDX Blog Code
 *
 */
const Mdx = (props: MdxProps) => {
  const { code } = props;
  const MDXContent = useMDXComponent(code);

  return (
    <div className={`prose w-full [&_li]:before:hidden`}>
      <MDXContent components={components} />
    </div>
  );
};

export default Mdx;
